<template lang="pug">
div(v-if="show")
    #swappable.order-layout.b-a.m-b-lg
        div.grid-row(v-for="(rows, yIndex) in gridLayout", :key="yIndex")
            grid-box.grid-col.box--isDraggable(
                v-for="(col, xIndex) in rows",
                :key="yIndex + xIndex",
                :x="xIndex",
                :y="yIndex",
                :style="{ width: boxWidth, height: boxHeight }"
            )
</template>

<script>
import { Swappable, Plugins } from '@shopify/draggable';
import { mapGetters, mapActions } from 'vuex';
import gridBox from './order-layout-box.vue';
import LoadingMixin from './mixins/loading';

export default {
    components: {
        gridBox,
    },

    mixins: [LoadingMixin],

    data: () => ({
        currentGridDragOver: null,
        prevGridDragOver: null,
        show: true,
        boxHeight: '150px',
    }),

    computed: {
        ...mapGetters('orderLayout', [
            'orderLayout',
            'getPageCellByGrid',
            'getPageById',
            'activePage',
        ]),

        gridLayout() {
            const grid = [];
            const height = this.orderLayout.height;
            const width = this.orderLayout.width;

            for (let i = 0; i < height; i++) {
                grid[i] = [];

                for (let j = 0; j < width; j++) {
                    grid[i].push(j);
                }
            }

            return grid;
        },

        boxWidth() {
            const percentageWidth = 100 / this.orderLayout.width;
            return `${percentageWidth}%`;
        },
    },

    mounted() {
        this.initSwappable();
        this.registerWindowResizeListener();
        this.registerClickListener();

        this.$nextTick(() => {
            this.resizeBoxHeight();
        });
    },

    updated() {
        this.$nextTick(() => {
            this.resizeBoxHeight();
            this.show = true;

            setTimeout(() => {
                if (_.isNil(this.swappable)) this.initSwappable();
            }, 100);
        });
    },

    methods: {
        ...mapActions('orderLayout', [
            'setOrCreatePageCell',
            'viewPage',
            'setSelectedGrid',
        ]),

        resizeBoxHeight() {
            const boxWidth = $('.order-body')
                .first()
                .width();

            if (!_.isNil(boxWidth)) this.boxHeight = `${boxWidth}px`;
        },

        initSwappable() {
            this.containers = document.querySelectorAll('#swappable .grid-col');
            this.swappable = new Swappable(this.containers, {
                draggable: '.box--isDraggable',
                mirror: {
                    constrainDimensions: true,
                    cursorOffsetX: 40,
                    cursorOffsetY: 40,
                },
                plugins: [Plugins.ResizeMirror],
            });

            this.swappable.on('swappable:swapped', e => {
                const { swappedElement, data } = e;
                const sourceElem = data.dragEvent.originalSource;

                if (this.currentGridDragOver !== null)
                    this.setPrevGridDragOver(this.currentGridDragOver);

                this.setCurrentGridDragOver(swappedElement.id);
            });

            this.swappable.on('swappable:stop', e => {
                const { originalSource } = e.dragEvent.data;

                if (this.currentGridDragOver !== this.prevGridDragOver) {
                    this.swapCell(
                        this.getGridByElemId(originalSource.id),
                        this.getGridByElemId(this.currentGridDragOver),
                    );

                    // set as selected grid on dragged element
                    const { x, y } = this.getGridByElemId(
                            this.currentGridDragOver,
                        ),
                        page_id = this.activePage.id;

                    this.setSelectedGrid({ page_id, x, y });
                }

                this.resetGridOver();
            });

            this.swappable.on('drag:start', e => {
                const { originalSource } = e.data;
                const [x, y] = originalSource.id.split(',');
                const cell = this.getPageCellByGrid(this.activePage.id, x, y);

                // disable drag for empty component
                if (
                    _.isNull(cell) ||
                    _.isNull(cell.action_type) ||
                    this.isLoading
                ) {
                    e.cancel();
                    return;
                }

                this.setSelectedGrid({ page_id: this.activePage.id, x, y });
            });
        },

        setCurrentGridDragOver(grid_id) {
            this.currentGridDragOver = grid_id;
        },

        setPrevGridDragOver(grid_id) {
            this.prevGridDragOver = grid_id;
        },

        resetGridOver() {
            this.prevGridDragOver = null;
            this.currentGridDragOver = null;
        },

        getGridByElemId(grid_id) {
            const [x, y] = grid_id.split(',');
            return { x, y };
        },

        getReplaceableCellAttrOrEmpty(cell) {
            if (_.isNull(cell)) return {};

            return _.pick(cell, [
                'width',
                'height',
                'color',
                'action_type',
                'action_id',
                'action',
            ]);
        },

        destroySwappable() {
            this.swappable.destroy();
            this.swappable = null;
        },

        swapCell(gridA, gridB) {
            const page_id = this.activePage.id;
            const cellA = this.getPageCellByGrid(page_id, gridA.x, gridA.y);
            const cellB = this.getPageCellByGrid(page_id, gridB.x, gridB.y);

            this.setOrCreatePageCell(
                _.assign(
                    {
                        page_id,
                        x: gridA.x,
                        y: gridA.y,
                    },
                    this.getReplaceableCellAttrOrEmpty(cellB),
                ),
            );

            this.setOrCreatePageCell(
                _.assign(
                    {
                        page_id,
                        x: gridB.x,
                        y: gridB.y,
                    },
                    this.getReplaceableCellAttrOrEmpty(cellA),
                ),
            );

            // item is being force to dragable destination and unable to sort by x,y index
            // temporary fix for this issue is to remove & reload the element container
            this.destroySwappable();
            this.show = false;
            this.$forceUpdate();
        },

        registerWindowResizeListener() {
            window.addEventListener('resize', () => {
                this.resizeBoxHeight();
            });
        },

        // handle outside click when has selected cell
        registerClickListener() {
            document.getElementById('order-layout').addEventListener(
                'click',
                e => {
                    const elem = $(e.target);

                    const childOfDragable = elem.parents('.box--isDraggable')
                        .length;

                    const childOfMenuAction = elem.parents('.cell-menu-action')
                        .length;

                    const hasClassMenuAction = elem.hasClass(
                        'cell-menu-action',
                    );

                    const hasEmptyBoxParent = elem.parents('.cell-is-empty')
                        .length;

                    if (childOfMenuAction || hasClassMenuAction) return;
                    if (childOfDragable && !hasEmptyBoxParent) return;

                    this.setSelectedGrid();
                },
                false,
            );
        },
    },
};
</script>



// WEBPACK FOOTER //
// resources/assets/js/franchise_component/order-layout/order-layout-grid.vue